import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import WalterLoader from '../shared/WalterLoader';
import Swal from 'sweetalert2';

import '../../styles/Audit.css';

import { DEFAULT_TECHNICAL_DATA, buildDefaultUsageForm } from './waterpointForm/constants';
import { usageHasSurface } from './waterpointForm/utils';
import { useWaterpointAPI } from './waterpointForm/useWaterpointAPI';
import { useEquipmentImage } from './waterpointForm/useEquipmentImage';
import { useOnlineStatus } from '../../offline/ConnectivityContext';
import { useAppMetadata } from '../shared/useAppMetadata';
import { enqueuePhotoUpload } from '../../offline/createApi';
import { compressImageFile } from '../../utils/imageCompression';
import { useBeforeUnloadWarning } from '../shared/useBeforeUnloadWarning';

import FaucetFields from './waterpointForm/FaucetFields';
import WCFields from './waterpointForm/WCFields';
import ShowerFields from './waterpointForm/ShowerFields';
import MachineFields from './waterpointForm/MachineFields';
import UrinalFields from './waterpointForm/UrinalFields';
import UsageForm from './waterpointForm/UsageForm';
import UsageList from './waterpointForm/UsageList';

const EQUIPMENT_TYPES = [
    { value: 'faucet',  label: 'Robinet / Évier', emoji: '🚰' },
    { value: 'wc',      label: 'WC',               emoji: '🚽' },
    { value: 'shower',  label: 'Douche',            emoji: '🚿' },
    { value: 'urinal',  label: 'Urinoir',           emoji: '🚹' },
    { value: 'machine', label: 'Machine technique', emoji: '⚙️' },
];

const WaterpointForm = forwardRef(function WaterpointForm({ sectionId: propSectionId, roomId: propRoomId, equipmentId: propEquipmentId, onClose, onCanSaveChange, onSavingChange, buildingData: propBuildingData, roomData: propRoomData }, ref) {
    const navigate = useNavigate();
    const params = useParams();
    const { isOnline } = useOnlineStatus();

    const id_client = params.id_client;
    const id_building = params.id_building;
    const id_section = propSectionId ?? params.id_section;
    const id_room = propRoomId ?? params.id_room;
    const id_equipment = propEquipmentId ?? params.id_equipment;
    const isEditMode = !!id_equipment;

    // ─── Loading & error ──────────────────────────────────────────────────────
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [loadingEquipment, setLoadingEquipment] = useState(isEditMode);
    const [loadingUsages, setLoadingUsages] = useState(isEditMode);
    const [loadingBuilding, setLoadingBuilding] = useState(true);
    const [error, setError] = useState(null);

    // ─── Remote data ──────────────────────────────────────────────────────────
    const [sectionName, setSectionName] = useState(null);
    const [buildingData, setBuildingData] = useState(propBuildingData ?? null);
    const [roomData, setRoomData] = useState(propRoomData ?? null);

    // ─── Equipment ────────────────────────────────────────────────────────────
    const [equipmentData, setEquipmentData] = useState(
        isEditMode
            ? {}
            : {
                _id: uuidv4(),
                type_equipment: '',
                location: { client_id: id_client, building_id: id_building, section_id: id_section, room_id: id_room },
                technical_data: {},
                water_value: null,
                water_value_unit: ''
            }
    );

    // ─── Usages ───────────────────────────────────────────────────────────────
    const [listAddedUsages, setListAddedUsages] = useState([]);
    // Ids des usages chargés à l'ouverture (mode édition) : l'API ne supprime que
    // ceux-là s'ils ont été retirés, jamais un usage créé ailleurs entre-temps
    // (autre onglet, autre auditeur, assistant d'audit).
    const loadedUsageIdsRef = useRef([]);
    const [usageForm, setUsageForm] = useState(
        isEditMode ? { type_usage: '', functional_unit: '', is_known_volume: false, usage_data: { nb_users: 0 } } : null
    );
    const [isEditing, setIsEditing] = useState(false);
    const [selectedUsageId, setSelectedUsageId] = useState(null);

    // ─── UI ───────────────────────────────────────────────────────────────────
    const [showUsageDetails, setShowUsageDetails] = useState(false);
    const [singleEquipmentView, setSingleEquipmentView] = useState(false);
    // La saisie des usages n'est pas l'objet de l'audit d'équipement — repliée
    // par défaut, tant qu'elle ne monte pas UsageForm ne charge pas la
    // taxonomie des usages pour rien.
    const [showUsageSection, setShowUsageSection] = useState(false);

    // ─── Image (local state mirrors old pattern) ─────────────────────────────
    const [imagePreview, setImagePreview] = useState(null);

    // ─── Hooks ────────────────────────────────────────────────────────────────
    const { fetchEquipmentData, fetchUsagesData, save } = useWaterpointAPI(isEditMode);
    const { imageToDeleteOnSave, pendingImage, selectNewImage, clearImage, uploadImage, deleteOldImage } = useEquipmentImage(isEditMode);
    // Nécessaire ici (en plus de MachineFields) pour valider les champs
    // personnalisés requis du type de machine sélectionné avant sauvegarde.
    const { data: machineTypes } = useAppMetadata('machine_types');


    // ─── Effects ──────────────────────────────────────────────────────────────

    useEffect(() => {
        if (!isEditMode && id_section) {
            fetch(`${process.env.REACT_APP_API_URL}/get_section_name/${id_section}`)
                .then((r) => r.json())
                .then(setSectionName)
                .catch((err) => setError(err.message));
        }
    }, [id_section]);

    useEffect(() => {
        if (propRoomData || !id_room) return;
        fetch(`${process.env.REACT_APP_API_URL}/get_room/${id_room}`)
            .then((r) => r.json())
            .then(setRoomData)
            .catch((err) => setError(err.message));
    }, [id_room]);

    useEffect(() => {
        if (propBuildingData) { setLoadingBuilding(false); return; }
        setLoadingBuilding(true);
        if (id_building) {
            fetch(`${process.env.REACT_APP_API_URL}/get_building/${id_building}`)
                .then((r) => r.json())
                .then(setBuildingData)
                .catch((err) => setError(err.message))
                .finally(() => setLoadingBuilding(false));
        }
    }, [id_building]);

    useEffect(() => {
        if (!isEditMode) return;
        setLoadingEquipment(true);
        fetchEquipmentData(id_equipment)
            .then((data) => {
                setEquipmentData(data);
                if (data.image_url) setImagePreview(data.image_url);
            })
            .catch(console.error)
            .finally(() => setLoadingEquipment(false));

        setLoadingUsages(true);
        fetchUsagesData(id_equipment)
            .then((usages) => {
                loadedUsageIdsRef.current = usages.map((usage) => usage._id);
                setListAddedUsages(usages);
            })
            .catch(console.error)
            .finally(() => setLoadingUsages(false));
    }, [id_equipment]);

    // Sync usageForm when equipment type changes (needed after fetch in edit mode)
    useEffect(() => {
        if (equipmentData.type_equipment) {
            setUsageForm(buildDefaultUsageForm(equipmentData.type_equipment, equipmentData._id, roomData, buildingData));
        }
    }, [equipmentData.type_equipment]);

    // ─── Equipment handlers ───────────────────────────────────────────────────

    const handleSelectType = (value) => {
        setIsDirty(true);
        setEquipmentData((prev) => ({
            ...prev,
            type_equipment: value,
            technical_data: value ? { ...DEFAULT_TECHNICAL_DATA[value] } : {}
        }));
        if (value) setUsageForm(buildDefaultUsageForm(value, equipmentData._id, roomData, buildingData));
    };

    const handleClearType = () => {
        Swal.fire({
            title: 'Changer de type ?',
            text: 'Les données techniques déjà saisies pour cet équipement seront effacées.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Oui, changer',
            cancelButtonText: 'Annuler',
            customClass: 'swal-custom',
        }).then((result) => {
            if (result.isConfirmed) {
                setEquipmentData((prev) => ({ ...prev, type_equipment: '', technical_data: {} }));
            }
        });
    };

    const handleChangeTechnical = (e) => {
        setIsDirty(true);
        const { name, value, type, checked } = e.target;
        const parsed = type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) : value);
        setEquipmentData((prev) => ({
            ...prev,
            technical_data: { ...prev.technical_data, [name]: parsed }
        }));
    };

    const handleChangeCustomField = (key, value) => {
        setIsDirty(true);
        setEquipmentData((prev) => ({
            ...prev,
            technical_data: {
                ...prev.technical_data,
                custom_fields: { ...prev.technical_data.custom_fields, [key]: value }
            }
        }));
    };

    // ─── Usage handlers ───────────────────────────────────────────────────────

    const handleUsageChange = (e) => {
        const { name, value, type, checked } = e.target;
        const parsed = type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) : value);
        setUsageForm((prev) => {
            if (name === 'daily_usage_type' || name in prev.usage_data) {
                return { ...prev, usage_data: { ...prev.usage_data, [name]: parsed } };
            }
            return { ...prev, [name]: parsed };
        });
    };

    const handleUserCategoryChange = (e) => {
        const value = e.target.value;
        const currentNbUsers = usageForm?.usage_data?.nb_users ?? 0;

        if (value === 'none') {
            setUsageForm((prev) => {
                if (!prev) return prev;
                const { user_id, user_name, ...rest } = prev;
                return { ...rest };
            });
            return;
        }

        const optionEl = e.target.options[e.target.selectedIndex];
        const user_id = optionEl.getAttribute('data-user-id');
        const userObj = buildingData.building_users.find((u) => u.user_id === user_id);
        if (!userObj) return;

        setUsageForm((prev) => ({
            ...prev,
            user_id,
            user_name: value,
            usage_data: {
                ...prev.usage_data,
                nb_users: currentNbUsers,
                daily_usage: userObj.mean_time_hand_wash ?? prev.usage_data.daily_usage,
                weekly_usage: userObj.weekly_usage ?? prev.usage_data.weekly_usage,
                yearly_usage: userObj.yearly_usage ?? prev.usage_data.yearly_usage,
                more_intense_weeks: userObj.more_intense_weeks ?? prev.usage_data.more_intense_weeks,
                less_intense_weeks: userObj.less_intense_weeks ?? prev.usage_data.less_intense_weeks,
            }
        }));
    };

    const handleUsageSubmit = () => {
        if (!usageForm.type_usage.trim() || !usageForm.functional_unit.trim()) {
            Swal.fire({ title: 'Erreur', text: "Veuillez remplir le type d'usage et l'unité fonctionnelle.", icon: 'error', confirmButtonText: 'OK', customClass: 'swal-custom' });
            return;
        }

        // Un champ numérique vidé vaut NaN (parseFloat('')), que JSON.stringify envoie
        // en null : les champs du calcul doivent être remplis, les semaines
        // intenses/réduites vides valent 0.
        const ud = usageForm.usage_data;
        const missing = usageForm.is_known_volume
            ? (Number.isFinite(usageForm.annual_volume) && usageForm.annual_volume > 0 ? [] : ['le volume annuel'])
            : [
                ['daily_usage', "l'usage quotidien"],
                ['weekly_usage', "l'usage hebdomadaire"],
                ['yearly_usage', "l'usage annuel"],
                ...((ud.daily_usage_type ?? 'per_person') === 'per_person' ? [['nb_users', "le nombre d'utilisateurs"]] : []),
            ].filter(([key]) => !Number.isFinite(ud[key])).map(([, label]) => label);
        if (missing.length) {
            Swal.fire({ title: 'Champ manquant', text: `Veuillez renseigner ${missing.join(', ')}.`, icon: 'error', confirmButtonText: 'OK', customClass: 'swal-custom' });
            return;
        }
        const usageData = Object.fromEntries(
            Object.entries(ud).map(([key, value]) => [key, Number.isNaN(value) ? 0 : value])
        );
        const updatedUsage = {
            ...usageForm,
            ...(usageHasSurface(usageForm) ? { surface_area: usageForm.surface_area || null } : {}),
            usage_data: {
                ...usageData,
                daily_usage_type: usageForm.usage_data.daily_usage_type ?? 'per_person',
                ...(equipmentData.type_equipment === 'machine' ? {
                    cycle_duration: usageForm.usage_data.cycle_duration || null,
                    daily_utilisation: usageForm.usage_data.daily_utilisation || null
                } : {})
            }
        };

        setIsDirty(true);
        if (isEditing) {
            setListAddedUsages((prev) => prev.map((u) => u._id === updatedUsage._id ? updatedUsage : u));
        } else {
            setListAddedUsages((prev) => [...prev, updatedUsage]);
        }

        setUsageForm(buildDefaultUsageForm(equipmentData.type_equipment, equipmentData._id, roomData, buildingData));
        setIsEditing(false);
        setSelectedUsageId(null);
        setShowUsageDetails(false);
    };

    const handleEditUsage = (usage) => {
        if (selectedUsageId === usage._id) {
            setUsageForm(buildDefaultUsageForm(equipmentData.type_equipment, equipmentData._id, roomData, buildingData));
            setIsEditing(false);
            setSelectedUsageId(null);
            setShowUsageSection(false);
        } else {
            setUsageForm(usage);
            setIsEditing(true);
            setSelectedUsageId(usage._id);
            setShowUsageSection(true);
        }
    };

    const handleDeleteUsage = (id) => {
        setIsDirty(true);
        setListAddedUsages((prev) => prev.filter((u) => u._id !== id));
        if (selectedUsageId === id) {
            setUsageForm(buildDefaultUsageForm(equipmentData.type_equipment, equipmentData._id, roomData, buildingData));
            setIsEditing(false);
            setSelectedUsageId(null);
        }
    };

    // ─── Image handlers ───────────────────────────────────────────────────────

    const handleImageChange = async (e) => {
        setIsDirty(true);
        const file = e.target.files[0];
        // Vide l'input pour qu'un même fichier re-choisi (après suppression)
        // redéclenche onChange.
        e.target.value = '';
        if (!file) return;
        // Downscale/re-encode before it ever reaches storage — keeps the
        // Cloudinary payload small online, and keeps the IndexedDB footprint
        // small when the photo has to sit queued on-device while offline.
        let compressed;
        try {
            compressed = await compressImageFile(file);
        } catch (err) {
            console.error("Lecture de la photo impossible :", err);
            Swal.fire({ title: 'Photo illisible', text: "Ce format d'image n'a pas pu être lu. Essayez avec une photo JPEG, PNG ou HEIC.", icon: 'error', confirmButtonText: 'OK', customClass: 'swal-custom' });
            return;
        }
        selectNewImage(compressed, equipmentData.image_url);
        const reader = new FileReader();
        reader.onloadend = () => setImagePreview(reader.result);
        reader.readAsDataURL(compressed);
        if (isEditMode) setEquipmentData((prev) => ({ ...prev, image_url: '' }));
    };

    const handleDeleteImageLocally = async (e) => {
        e.preventDefault();
        const confirmed = await clearImage(equipmentData.image_url);
        if (confirmed) {
            // La suppression n'est appliquée qu'à l'enregistrement : sans ça,
            // fermer le formulaire ne prévient pas et la photo revient.
            setIsDirty(true);
            setImagePreview(null);
            if (isEditMode) setEquipmentData((prev) => ({ ...prev, image_url: '' }));
        }
    };

    // ─── Save ─────────────────────────────────────────────────────────────────

    // Miroir de api/equipment_rules.py:form_errors. Un champ vidé vaut NaN
    // (parseFloat('')) : il doit bloquer, sinon il est enregistré en null.
    const waterValueFilled = () => {
        const td = equipmentData.technical_data;
        const positive = (v) => Number.isFinite(v) && v > 0;
        switch (equipmentData.type_equipment) {
            case 'wc': {
                const ft = td.flush_type;
                if (ft === 'dual_flush') return positive(td.flush_volume_max) && positive(td.flush_volume_min);
                if (ft === 'interruptible_flush') return positive(td.flush_flow);
                return positive(td.flush_volume_max);
            }
            case 'faucet': case 'shower': return positive(td.max_flow);
            case 'machine': return Number.isFinite(td.volume_per_cycle);
            case 'urinal': return positive(td.flush_volume);
            default: return false;
        }
    };

    const handleSaveButtonClick = async () => {
        if (isSaving) return;
        if (!equipmentData.type_equipment) {
            Swal.fire({ title: 'Erreur', text: "Veuillez choisir un type d'équipement.", icon: 'error', confirmButtonText: 'OK', customClass: 'swal-custom' });
            return;
        }
        if (!waterValueFilled()) {
            Swal.fire({ title: 'Erreur', text: "Veuillez remplir la valeur d'eau pour cet équipement.", icon: 'error', confirmButtonText: 'OK', customClass: 'swal-custom' });
            return;
        }
        const td = equipmentData.technical_data;
        if (!(Number.isInteger(td.nb_equipment) && td.nb_equipment >= 1)) {
            Swal.fire({ title: 'Champ manquant', text: "Le nombre d'équipements doit être un entier supérieur ou égal à 1.", icon: 'error', confirmButtonText: 'OK', customClass: 'swal-custom' });
            return;
        }
        if (equipmentData.type_equipment === 'faucet' && !td.faucet_type) {
            Swal.fire({ title: 'Champ manquant', text: "Veuillez sélectionner un type de robinet.", icon: 'error', confirmButtonText: 'OK', customClass: 'swal-custom' });
            return;
        }
        if (['faucet', 'shower'].includes(equipmentData.type_equipment) && td.activation_mode === 'push_button' && !td.timer_duration) {
            Swal.fire({ title: 'Champ manquant', text: "Veuillez renseigner la durée du minuteur.", icon: 'error', confirmButtonText: 'OK', customClass: 'swal-custom' });
            return;
        }
        if (equipmentData.type_equipment === 'machine') {
            if (!td.type_machine) {
                Swal.fire({ title: 'Champ manquant', text: "Veuillez sélectionner un type de machine.", icon: 'error', confirmButtonText: 'OK', customClass: 'swal-custom' });
                return;
            }
            const selectedMachineType = machineTypes.find((t) => t.value === td.type_machine);
            const missingField = selectedMachineType?.fields?.find((f) => {
                if (!f.required) return false;
                const value = td.custom_fields?.[f.key];
                return value === null || value === undefined || value === '';
            });
            if (missingField) {
                Swal.fire({ title: 'Champ manquant', text: `Veuillez renseigner le champ "${missingField.label}".`, icon: 'error', confirmButtonText: 'OK', customClass: 'swal-custom' });
                return;
            }
        }
        if (isEditing) {
            const result = await Swal.fire({ title: 'Attention', text: 'Vous avez des usages non enregistrés. Voulez-vous vraiment continuer ?', icon: 'warning', showCancelButton: true, confirmButtonText: 'Oui, continuer', cancelButtonText: 'Non, annuler', customClass: 'swal-custom' });
            if (result.isConfirmed) await runSave();
        } else {
            await runSave();
        }
    };

    // Wraps persist() with a visible saving state and a safety net: on a flaky
    // connection the POST can hang for seconds or reject outright, and with no
    // feedback the user has no way to tell a click registered from one that
    // silently failed (and was tempted to click again, double-submitting).
    const runSave = async () => {
        setIsSaving(true);
        try {
            await persist();
        } catch (err) {
            console.error("Erreur lors de l'enregistrement du point d'eau :", err);
            Swal.fire({
                title: "Échec de l'enregistrement",
                text: "La sauvegarde n'a pas abouti (connexion instable ?). Vos informations sont conservées, vérifiez votre réseau et réessayez.",
                icon: 'error',
                confirmButtonText: 'OK',
                customClass: 'swal-custom'
            });
        } finally {
            setIsSaving(false);
        }
    };

    const persist = async () => {
        if (isEditMode) await deleteOldImage();
        // Edit mode stays online-only in v1 (see useWaterpointAPI.js), so a
        // picked photo there can only exist while online — upload it now as
        // before. In create mode, a photo picked offline is queued below
        // instead, once the equipment it belongs to has been persisted.
        const imageUrl = isOnline ? await uploadImage() : null;
        const finalEquipmentData = imageUrl ? { ...equipmentData, image_url: imageUrl } : equipmentData;
        const result = await save(finalEquipmentData, listAddedUsages, { isOnline, loadedUsageIds: loadedUsageIdsRef.current });
        if (!isOnline && !isEditMode && pendingImage) {
            await enqueuePhotoUpload(finalEquipmentData._id, pendingImage);
        }
        backToBuildingData(result?.offline ? (result.equipmentData || finalEquipmentData) : null);
    };

    const handleBackButtonClick = () => {
        if (isDirty) {
            Swal.fire({
                title: 'Attention',
                text: "Vous avez des modifications non enregistrées. Voulez-vous vraiment fermer ?",
                icon: 'warning', showCancelButton: true,
                confirmButtonText: 'Oui, fermer', cancelButtonText: 'Non, annuler', customClass: 'swal-custom'
            }).then((result) => { if (result.isConfirmed) backToBuildingData(); });
        } else {
            backToBuildingData();
        }
    };

    const backToBuildingData = (savedEquipment = null) => {
        if (onClose) {
            // When the save happened offline, pass back what was just written so
            // the parent can merge it into local state without a refetch (the
            // GETs would fail offline anyway).
            onClose(savedEquipment ? { ...savedEquipment, usages: listAddedUsages.map((u) => u.type_usage) } : undefined);
        } else {
            navigate(`/audit/${id_client}/${id_building}`, { state: { initialSectionId: id_section, initialRoomId: id_room } });
        }
    };

    // Covers accidental reload/close/tab-close/back/typed-URL navigation.
    useBeforeUnloadWarning(isDirty);

    // ─── Sheet mode plumbing ─────────────────────────────────────────────────

    useImperativeHandle(ref, () => ({
        triggerSave: handleSaveButtonClick,
        triggerClose: handleBackButtonClick,
    }));

    useEffect(() => {
        onCanSaveChange?.(!!equipmentData.type_equipment);
    }, [equipmentData.type_equipment]);

    useEffect(() => {
        onSavingChange?.(isSaving);
    }, [isSaving]);

    // ─── Derived values ───────────────────────────────────────────────────────

    const functionalUnits = buildingData?.functional_units ?? [];
    const nbEquipments = equipmentData?.technical_data?.nb_equipment || 1;
    const type = equipmentData.type_equipment;
    // Détermine si l'usage de cet équipement se compte en minutes (débit,
    // L/min) ou en cycles (volume par cycle, L/cycle) — WC/urinoir n'ont pas
    // ce choix, toujours en cycles.
    const waterValueUnit = type === 'faucet' || type === 'shower'
        ? equipmentData.technical_data?.max_flow_unit
        : type === 'machine'
        ? equipmentData.technical_data?.volume_per_cycle_unit
        : null;
    const titleLocation = isEditMode
        ? `${equipmentData.location?.name_room}, ${equipmentData.location?.name_section}`
        : `${roomData?.name_room}, ${sectionName}`;

    const isLoading = isEditMode && (loadingEquipment || loadingUsages || loadingBuilding);

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <div className="audit-container">
            {isLoading ? (
                <div className="centered-spinner">
                    <WalterLoader />
                </div>
            ) : (
                <div>
                    <div className="wf-header">
                        <p className="wf-breadcrumb">{titleLocation}</p>
                        {roomData?.nb_identical_rooms > 1 && (
                            <p className="wf-room-multiplier-hint">
                                Cette pièce représente {roomData.nb_identical_rooms} pièces identiques.
                                Les valeurs saisies ci-dessous concernent une seule pièce ; elles seront
                                automatiquement multipliées par {roomData.nb_identical_rooms} pour les
                                totaux du bâtiment.
                            </p>
                        )}
                        <div className="wf-header-row">
                            <h1 className="wf-title">
                                {isEditMode ? "Modifier l'équipement" : "Nouveau point d'eau"}
                            </h1>
                            <span className={`wf-badge ${isEditMode ? 'wf-badge--edit' : 'wf-badge--new'}`}>
                                {isEditMode ? 'Édition' : 'Création'}
                            </span>
                        </div>
                    </div>

                    <div className="audit-container-in">
                        <div className="audit-container-forms">

                            {/* ── Equipment form ── */}
                            <div className="audit-form-container">
                                <h2>Informations sur l'équipement</h2>
                                <form>
                                    {!type ? (
                                        <div className="equip-type-grid">
                                            {EQUIPMENT_TYPES.map(t => (
                                                <button key={t.value} type="button" className="equip-type-card" onClick={() => handleSelectType(t.value)}>
                                                    <span className="equip-type-emoji">{t.emoji}</span>
                                                    <span className="equip-type-name">{t.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="equip-type-chosen">
                                            {(() => {
                                                const t = EQUIPMENT_TYPES.find(e => e.value === type);
                                                return (
                                                    <div className="equip-type-chosen-chip">
                                                        <span className="equip-type-emoji">{t?.emoji}</span>
                                                        <span className="equip-type-chosen-name">{t?.label}</span>
                                                    </div>
                                                );
                                            })()}
                                            <button type="button" className="equip-type-change-btn" onClick={handleClearType}>
                                                Changer
                                            </button>
                                        </div>
                                    )}
                                    {type === 'faucet' && (
                                        <FaucetFields
                                            data={equipmentData.technical_data}
                                            onChange={handleChangeTechnical}
                                            imagePreview={imagePreview}
                                            onImageChange={handleImageChange}
                                            onDeleteImage={handleDeleteImageLocally}
                                            photoDisabled={isEditMode && !isOnline}
                                        />
                                    )}
                                    {type === 'wc' && (
                                        <WCFields
                                            data={equipmentData.technical_data}
                                            onChange={handleChangeTechnical}
                                            imagePreview={imagePreview}
                                            onImageChange={handleImageChange}
                                            onDeleteImage={handleDeleteImageLocally}
                                            photoDisabled={isEditMode && !isOnline}
                                        />
                                    )}
                                    {type === 'shower' && (
                                        <ShowerFields
                                            data={equipmentData.technical_data}
                                            onChange={handleChangeTechnical}
                                            imagePreview={imagePreview}
                                            onImageChange={handleImageChange}
                                            onDeleteImage={handleDeleteImageLocally}
                                            photoDisabled={isEditMode && !isOnline}
                                        />
                                    )}
                                    {type === 'machine' && (
                                        <MachineFields
                                            data={equipmentData.technical_data}
                                            onChange={handleChangeTechnical}
                                            onChangeCustomField={handleChangeCustomField}
                                            imagePreview={imagePreview}
                                            onImageChange={handleImageChange}
                                            onDeleteImage={handleDeleteImageLocally}
                                            photoDisabled={isEditMode && !isOnline}
                                        />
                                    )}
                                    {type === 'urinal' && (
                                        <UrinalFields
                                            data={equipmentData.technical_data}
                                            onChange={handleChangeTechnical}
                                            imagePreview={imagePreview}
                                            onImageChange={handleImageChange}
                                            onDeleteImage={handleDeleteImageLocally}
                                            photoDisabled={isEditMode && !isOnline}
                                        />
                                    )}
                                </form>
                            </div>

                            {/* ── Usage form — repliée par défaut (pas l'objet de l'audit) ── */}
                            {type && usageForm && (
                                <>
                                    <button
                                        type="button"
                                        className="usage-section-toggle-card"
                                        onClick={() => setShowUsageSection((p) => !p)}
                                    >
                                        <span className="usage-section-toggle-left">
                                            <span className={`arrow-icon ${showUsageSection ? 'down' : 'right'}`} />
                                            {showUsageSection ? 'Masquer la saisie des usages' : "Ajouter ou modifier un usage"}
                                        </span>
                                        {!showUsageSection && listAddedUsages.length > 0 && (
                                            <span className="usage-section-count-badge">{listAddedUsages.length}</span>
                                        )}
                                    </button>

                                    {showUsageSection && (
                                        <>
                                            <UsageForm
                                                usageForm={usageForm}
                                                equipmentType={type}
                                                equipmentTechnicalData={equipmentData.technical_data}
                                                waterValueUnit={waterValueUnit}
                                                nbEquipments={nbEquipments}
                                                buildingId={id_building}
                                                buildingData={buildingData}
                                                functionalUnits={functionalUnits}
                                                isEditing={isEditing}
                                                singleEquipmentView={singleEquipmentView}
                                                setSingleEquipmentView={setSingleEquipmentView}
                                                showUsageDetails={showUsageDetails}
                                                toggleUsageDetails={() => setShowUsageDetails((p) => !p)}
                                                onUsageChange={handleUsageChange}
                                                onUserCategoryChange={handleUserCategoryChange}
                                                onSubmit={handleUsageSubmit}
                                            />
                                            <UsageList
                                                usages={listAddedUsages}
                                                functionalUnits={functionalUnits}
                                                selectedUsageId={selectedUsageId}
                                                onEdit={handleEditUsage}
                                                onDelete={handleDeleteUsage}
                                            />
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </div>

                    {!onClose && (
                        <div className='two-buttons'>
                            <button className="save-button-medium" onClick={handleSaveButtonClick} disabled={isSaving}>
                                {isSaving && <span className="btn-spinner" />}
                                {isSaving ? 'Enregistrement...' : "Enregistrer l'équipement et ses usages"}
                            </button>
                            <button className="save-button-medium" onClick={handleBackButtonClick} disabled={isSaving}>Retour vers le bâtiment</button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
});

export default WaterpointForm;
